import { updateProject } from "Actions";
import { ApiCurrency } from "@incendium/api";
import {
  ArrowForward,
  AutoAwesome,
  Inventory2,
  People,
  ShoppingCart,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import TimezoneSelect from "Components/TimezoneSelect/TimezoneSelect";
import { CurrencyPicker } from "features/currency";
import { saveProject } from "features/project";
import { useWooStoreStats } from "features/onboarding/woocommerce/hooks/useWooStoreStats";
import { useNotification, useSelectedProject } from "Hooks";
import { useCallback, useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch } from "react-redux";

const StatCard = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: "center",
  padding: theme.spacing(2.5, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
}));

const StatIconWrap = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: "1.125rem",
});

interface IEcomPlatformOnboardingStep3Props {
  onContinue: () => void;
  platformName?: string;
}

function EcomPlatformOnboardingStep3({
  onContinue,
  platformName = "WooCommerce",
}: IEcomPlatformOnboardingStep3Props) {
  const dispatch = useDispatch();
  const { selectedProject } = useSelectedProject();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { stats, loading: statsLoading } = useWooStoreStats(
    selectedProject?.id as number | undefined,
  );

  const [description, setDescription] = useState(
    selectedProject?.overview || "",
  );
  const [timezone, setTimezone] = useState(selectedProject?.timezone || "");
  const [currency, setCurrency] = useState<ApiCurrency>(
    selectedProject?.currency || ApiCurrency.USD,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDescription(selectedProject?.overview || "");
  }, [selectedProject?.overview]);

  useEffect(() => {
    setTimezone(selectedProject?.timezone || "");
  }, [selectedProject?.timezone]);

  useEffect(() => {
    setCurrency(selectedProject?.currency || ApiCurrency.USD);
  }, [selectedProject?.currency]);

  const formatCount = (n: number | undefined) => {
    if (statsLoading) {
      return null;
    }
    if (n === undefined || n === null) {
      return "—";
    }
    return n.toLocaleString();
  };

  const dirty =
    description !== (selectedProject?.overview || "") ||
    timezone !== (selectedProject?.timezone || "") ||
    currency !== (selectedProject?.currency || ApiCurrency.USD);

  const onSaveChanges = useCallback(async () => {
    if (!selectedProject || !dirty) {
      return;
    }
    setSaving(true);
    try {
      const res = await saveProject(selectedProject.clientId as number, {
        ...selectedProject,
        overview: description,
        timezone,
        currency,
      });
      dispatch(updateProject(res));
      showSuccessNotification("Information Saved");
    } catch {
      showErrorNotification(
        "There was an error saving this information, please try again",
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedProject,
    description,
    timezone,
    currency,
    dirty,
    dispatch,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const renderStatValue = (value: number | undefined) => {
    const formatted = formatCount(value);
    if (statsLoading) {
      return <CircularProgress size={28} />;
    }
    return (
      <Typography variant="h5" fontWeight={700}>
        {formatted}
      </Typography>
    );
  };

  return (
    <ValidatorForm onSubmit={onSaveChanges}>
      <Stack spacing={4}>
        <Box>
          <SectionTitle>Scan summary</SectionTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            We are capturing a lot of data from your{" "}
            <Box
              component="span"
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              {platformName}
            </Box>{" "}
            store.
          </Typography>
          <Stack direction="row" mt={3} sx={{ flexWrap: "wrap" }} gap={2}>
            <StatCard>
              <StatIconWrap>
                <Inventory2 />
              </StatIconWrap>
              {renderStatValue(stats?.productsCount)}
              <Typography variant="body2" color="text.secondary">
                Products found
              </Typography>
            </StatCard>
            <StatCard>
              <StatIconWrap>
                <People />
              </StatIconWrap>
              {renderStatValue(stats?.customersCount)}
              <Typography variant="body2" color="text.secondary">
                Customers found
              </Typography>
            </StatCard>
            <StatCard sx={{ flex: 1 }}>
              <StatIconWrap>
                <ShoppingCart />
              </StatIconWrap>
              {renderStatValue(stats?.transactionsCount)}
              <Typography variant="body2" color="text.secondary">
                Transactions found
              </Typography>
            </StatCard>
          </Stack>
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesome sx={{ color: "primary.main", fontSize: 20 }} />
            <SectionTitle>Store settings</SectionTitle>
          </Stack>

          <Typography variant="body2" color="text.secondary" mt={0.5} mb={2}>
            Confirm the timezone and currency used for your reporting.
          </Typography>
          <Stack spacing={4}>
            <TimezoneSelect
              value={timezone}
              size="medium"
              label="Select Store Timezone"
              onChange={(e) => setTimezone(e.target.value)}
            />
            <CurrencyPicker
              currency={currency}
              setCurrency={setCurrency}
              size="medium"
              label="Currency Symbol"
            />
          </Stack>
        </Box>

        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            size="large"
            type="submit"
            disabled={!dirty || saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForward />}
            onClick={onContinue}
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    </ValidatorForm>
  );
}

export default EcomPlatformOnboardingStep3;
